/*
 * "You may also like" — fully automatic, no merchant product picker.
 *
 * Per-item, lazily-loaded feed: every distinct product in the cart
 * (computed server-side in snippets/cart-drawer-recommendations.liquid,
 * newest-added first, so a just-added product's own recommendations land at
 * the top) contributes its own recommendations, fetched from
 * Shopify's public /recommendations/products.json endpoint — no Storefront
 * API access token needed, since it's a first-party route (same mechanism
 * sections/product-recommendations.liquid already uses via
 * routes.product_recommendations_url, just the JSON variant).
 *
 * Rather than fetching every cart product's recommendations up front (one
 * request each — wasteful for carts with many line items), only enough are
 * eagerly loaded to fill the initial view; the rest sit in a queue and are
 * fetched one at a time as an IntersectionObserver sentinel at the end of
 * the list scrolls into view.
 *
 * Nothing renders for an empty cart — there's nothing to recommend from
 * (see the render gate in snippets/cart-drawer.liquid).
 *
 * No persistent caching (no sessionStorage) — each cart product's fetched
 * result is kept in a plain in-memory object (recsByProductId below) for
 * the lifetime of the page only. Any change to the cart's distinct products
 * triggers a full rebuild of the list from the current set: already-fetched
 * products resolve straight from that in-memory store (no network
 * request); only genuinely new ones hit the network. A product still
 * recommended by whatever's currently in the cart simply reappears in the
 * rebuild — no special-case bookkeeping needed to "keep" it when a
 * different cart product is removed.
 */
(function () {
  'use strict';

  if (window.__cartRecommendationsInit) return;
  window.__cartRecommendationsInit = true;

  var CART_ICON_SVG =
    '<svg width="22.2" height="21.2" viewBox="0 0 22.2 21.2" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<path d="M0.6 0.6H3.6L6.3 13.1C6.40922 13.5233 6.65415 13.8992 6.99729 14.1701C7.34043 14.441 7.76289 14.592 8.2 14.6H18C18.4542 14.6144 18.8997 14.4736 19.2632 14.201C19.6268 13.9284 19.8866 13.54 20 13.1L21.6 5.6H4.7" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M7.6 20.6C8.15228 20.6 8.6 20.1523 8.6 19.6C8.6 19.0477 8.15228 18.6 7.6 18.6C7.04772 18.6 6.6 19.0477 6.6 19.6C6.6 20.1523 7.04772 20.6 7.6 20.6Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M18.6 20.6C19.1523 20.6 19.6 20.1523 19.6 19.6C19.6 19.0477 19.1523 18.6 18.6 18.6C18.0477 18.6 17.6 19.0477 17.6 19.6C17.6 20.1523 18.0477 20.6 18.6 20.6Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';
  // Shown in place of the product image when a recommended product has
  // none, rather than an empty or broken box.
  var IMAGE_PLACEHOLDER_SVG =
    '<svg class="cart-recommendation-card__placeholder" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<rect width="100" height="100" fill="currentColor" fill-opacity="0.04"/>' +
    '<path d="M27 66l14.5-18 10 12L60 46l13 20H27z" fill="currentColor" fill-opacity="0.15"/>' +
    '<circle cx="37" cy="38" r="6" fill="currentColor" fill-opacity="0.15"/>' +
    '</svg>';

  // Prefer the theme's product placeholder (rendered in Liquid), falling back
  // to the inline SVG above if the template isn't on the page.
  function getImagePlaceholder() {
    var template = document.querySelector('[data-image-placeholder]');
    return template && template.innerHTML.trim() ? template.innerHTML : IMAGE_PLACEHOLDER_SVG;
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function moneyFormat(amount) {
    var value = parseFloat(amount);
    if (isNaN(value)) return '';
    var currency = window.shopCurrency || 'USD';
    var locale = window.shopLocale || document.documentElement.lang || 'en';
    // French uses Canadian French formatting ("3 849,00 $")
    if (/^fr/i.test(locale)) locale = 'fr-CA';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency, currencyDisplay: 'narrowSymbol' }).format(value);
    } catch (e) {
      return '$' + value.toFixed(2);
    }
  }

  // In-memory only (no sessionStorage — cleared on page reload), keyed by
  // cart product id. Holds each product's raw (pre-exclusion) fetched
  // recommendation list, so re-fetching the same product within the same
  // page load is never necessary — every rebuild checks here first and
  // only hits the network for products not yet present.
  var recsByProductId = {};

  function fetchJson(url) {
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.ok ? response.json() : { products: [] }; })
      .catch(function () { return { products: [] }; });
  }

  /*
   * /recommendations/products.json prices in cents with images as plain URL
   * strings — normalized here into the shape renderCard expects.
   */
  function normalizeRecommendation(product) {
    var variant = (product.variants || []).find(function (v) { return v.available; }) || (product.variants || [])[0];
    if (!variant) return null;
    var compareAt = variant.compare_at_price;
    return {
      id: product.id,
      title: product.title,
      handle: product.handle,
      image: (product.images && product.images[0]) || product.featured_image || '',
      variantId: variant.id,
      price: (variant.price || 0) / 100,
      compareAtPrice: compareAt && compareAt > variant.price ? compareAt / 100 : null,
    };
  }

  // Shopify's /recommendations/products.json endpoint doesn't return more
  // than this regardless of a higher `limit` param — used as the request
  // size when the merchant hasn't set settings.recommendation_limit (0 here
  // means "no cap": show whatever Shopify's algorithm returns).
  var MAX_RECS_PER_PRODUCT = 10;

  /*
   * `excludeIds` filters out anything already in the cart or already
   * rendered for a different cart product in the current rebuild, so the
   * feed never shows the same product twice.
   */
  function fetchProductRecs(productId, intent, limit, excludeIds) {
    var promise = recsByProductId[productId]
      ? Promise.resolve(recsByProductId[productId])
      : fetchJson('/recommendations/products.json?product_id=' + encodeURIComponent(productId) +
          '&limit=' + (limit > 0 ? Math.max(limit, 6) : MAX_RECS_PER_PRODUCT) + '&intent=' + encodeURIComponent(intent))
          .then(function (r) { return ((r && r.products) || []).map(normalizeRecommendation).filter(Boolean); });

    return promise.then(function (products) {
      if (!recsByProductId[productId]) recsByProductId[productId] = products;
      var excluded = {};
      excludeIds.forEach(function (id) { excluded[String(id)] = true; });
      var filtered = products.filter(function (p) { return !excluded[String(p.id)]; });
      return limit > 0 ? filtered.slice(0, limit) : filtered;
    });
  }

  function renderCard(product, addToCartLabelTemplate, addToCartText) {
    var price = moneyFormat(product.price);
    var compareAt = product.compareAtPrice ? moneyFormat(product.compareAtPrice) : '';
    var label = (addToCartLabelTemplate || 'Add [product_title] to cart').replace('[product_title]', product.title);
    var image = product.image && product.image.indexOf('//') === 0 ? 'https:' + product.image : product.image;

    var el = document.createElement('div');
    el.className = 'cart-recommendation-card';
    el.innerHTML =
      '<a href="/products/' + product.handle + '" class="cart-recommendation-card__image">' +
        (image
          ? '<img src="' + escapeHtml(image) + '" alt="' + escapeHtml(product.title) + '" loading="lazy" width="176" height="176">'
          : getImagePlaceholder()) +
      '</a>' +
      '<div class="cart-recommendation-card__info">' +
        '<a href="/products/' + product.handle + '" class="cart-recommendation-card__title">' + escapeHtml(product.title) + '</a>' +
        '<div class="cart-recommendation-card__price">' +
          '<span class="cart-recommendation-card__price-now">' + price + '</span>' +
          (compareAt ? '<span class="cart-recommendation-card__price-was">' + compareAt + '</span>' : '') +
        '</div>' +
      '</div>' +
      '<button type="button" class="cart-recommendation-card__add" data-variant-id="' + product.variantId + '" aria-label="' + escapeHtml(label) + '">' +
        '<span class="cart-recommendation-card__add-icon">' + CART_ICON_SVG + '</span>' +
        '<span class="cart-recommendation-card__add-text">' + escapeHtml(addToCartText || 'Add to cart') + '</span>' +
      '</button>';
    return el;
  }

  // Elements patched in place after a recommendation add, instead of the
  // full cart-drawer.renderContents() teardown/rebuild (which recreates
  // every existing item row, the recommendations list, and the footer —
  // visibly flashing all of it for what is really a one-line change).
  // Deliberately excludes anything under .cart-recommendations: that block
  // is left alone here and updated separately (see syncFromPatchedState
  // in addToCart), not by re-fetching everything else too.
  var PATCH_SELECTORS = [
    '.drawer__heading-count',
    '.drawer__free-shipping',
    '#CartDrawer-CartItems',
    '.totals__subtotal-value',
  ];

  /*
   * Applies only the pieces of a fresh cart-drawer render that can actually
   * have changed after adding one item to an already non-empty cart. Returns
   * false (caller should fall back to the full renderContents()) when the
   * cart was empty beforehand, since that's a structural change — the empty
   * state's markup and the item-list markup are different subtrees, not a
   * content difference within the same one.
   */
  function patchCartDrawer(parsedState) {
    var html = parsedState.sections && parsedState.sections['cart-drawer'];
    if (!html) return false;

    var wasEmpty = document.querySelector('cart-drawer.is-empty') ||
      document.querySelector('.drawer__inner.is-empty');
    if (wasEmpty) return false;

    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var patchedAny = false;

    PATCH_SELECTORS.forEach(function (selector) {
      var target = document.querySelector(selector);
      var source = parsed.querySelector(selector);
      if (target && source) {
        target.innerHTML = source.innerHTML;
        patchedAny = true;
      }
    });

    // The cart-level protection plan depends on the cart's value, so adding an
    // item can change which plan variant applies (or whether one is offered at
    // all). Replace the whole <protection-plan-toggle> rather than patching its
    // innards: the fresh element re-runs its own sync (assets/cart-protection-
    // plan.js), which swaps the plan line to the matching variant if needed.
    var planTarget = document.querySelector('#CartDrawer protection-plan-toggle');
    var planSource = parsed.querySelector('protection-plan-toggle');
    if (planSource) {
      var freshPlan = document.importNode(planSource, true);
      if (planTarget) {
        planTarget.replaceWith(freshPlan);
      } else {
        var bottom = document.querySelector('#CartDrawer .cart-drawer__bottom');
        if (bottom) bottom.insertBefore(freshPlan, bottom.firstChild);
      }
    } else if (planTarget) {
      planTarget.remove();
    }

    var checkoutBtn = document.getElementById('CartDrawer-Checkout');
    var sourceCheckoutBtn = parsed.getElementById('CartDrawer-Checkout');
    if (checkoutBtn && sourceCheckoutBtn) {
      if (checkoutBtn.tagName !== sourceCheckoutBtn.tagName) {
        // Keep the checkout control in step with the freshly rendered cart.
        checkoutBtn.replaceWith(sourceCheckoutBtn.cloneNode(true));
      } else {
        checkoutBtn.disabled = sourceCheckoutBtn.disabled;
        if (sourceCheckoutBtn.href) checkoutBtn.href = sourceCheckoutBtn.href;
      }
    }

    var cartIconBubbleHtml = parsedState.sections['cart-icon-bubble'];
    if (cartIconBubbleHtml) {
      var bubbleTarget = document.getElementById('cart-icon-bubble');
      var bubbleParsed = new DOMParser().parseFromString(cartIconBubbleHtml, 'text/html');
      var bubbleSource = bubbleParsed.querySelector('.shopify-section');
      if (bubbleTarget && bubbleSource) bubbleTarget.innerHTML = bubbleSource.innerHTML;
    }

    return patchedAny;
  }

  /*
   * patchCartDrawer() leaves the live .cart-recommendations__list alone, but
   * the server-rendered cart-drawer section in parsedState already has the
   * product list recomputed from the post-add cart (see snippets/cart-drawer-
   * recommendations.liquid) — that's the freshest read of "what's in the
   * cart now" without a redundant second request. Sync the live list's
   * data-* to match and let a normal syncList() pass rebuild: the product
   * just added is now part of the cart, so it's excluded from recommending
   * itself and the cart signature changes, triggering a fresh rebuild.
   */
  function syncFromPatchedState(parsedState, list) {
    var html = parsedState.sections && parsedState.sections['cart-drawer'];
    if (!html) return;

    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-recommendations]');
    if (!fresh) return;

    list.dataset.cartProductIds = fresh.dataset.cartProductIds || '';
    syncList(list);
  }

  // Module-level (not per-DOM-node — the container is recreated on every
  // full cart-drawer re-render) state for the single recommendations list
  // on the page.
  //
  // loadedForProductIds/pendingProductIds hold cart product ids, in
  // newest-added-first order: which ones have already had their
  // recommendations fetched+rendered, and which are still queued. Processing
  // the queue front-to-back means the most recently added cart product's
  // recommendations load first and land at the top of the list.
  // renderedProductIds is a different set — the ids of the RECOMMENDED
  // cards currently shown — used to dedupe if two different cart products
  // both recommend the same thing. cartSignature is the last-rebuilt cart
  // product list (joined) so an irrelevant DOM mutation (e.g. this same
  // container just being recreated with unchanged cart contents) doesn't
  // trigger a pointless rebuild — an actual cart change always differs and
  // always rebuilds.
  var STATE = {
    loadedForProductIds: [],
    pendingProductIds: [],
    renderedProductIds: {},
    cartSignature: '',
    loading: false,
    observer: null,
    meta: {},
  };

  // How many cart products to eagerly load recommendations for (skip the
  // lazy-load queue) so the list doesn't render empty before the user has
  // scrolled at all.
  var EAGER_LOAD_COUNT = 2;

  // Scroll position (vertical list on desktop, horizontal row on mobile),
  // since the container itself is recreated along with everything else on
  // every .drawer__inner replace — restoring content in place doesn't help
  // if the fresh element still starts scrolled to 0. `scroll` doesn't bubble
  // in all browsers, so this listens on the capture phase instead.
  var scrollPosition = null;

  // Figma positions this block differently per breakpoint — a sidebar
  // beside the panel on desktop, inline between the item list and the
  // footer on mobile. Rather than rendering two DOM copies (one hidden by
  // CSS at each breakpoint), a single #cart-recommendations element is
  // physically relocated between two slots: .drawer__inner (desktop,
  // rendered there by default in snippets/cart-drawer.liquid) and
  // #cart-recommendations-mobile-slot (inside .drawer__footer). Every full
  // .drawer__inner replace (quantity/remove/protection-plan edits) recreates
  // the element fresh in the desktop slot, so this re-runs on every init()
  // call to correct the position again — cheap no-op when already correct.
  var MOBILE_QUERY = '(max-width: 989px)';

  function moveToViewportSlot() {
    var rec = document.getElementById('cart-recommendations');
    if (!rec) return;

    var isMobile = window.matchMedia(MOBILE_QUERY).matches;
    if (isMobile) {
      var mobileSlot = document.getElementById('cart-recommendations-mobile-slot');
      if (mobileSlot && rec.parentNode !== mobileSlot) mobileSlot.appendChild(rec);
    } else {
      // Scoped to #CartDrawer specifically — snippets/burger-drawer.liquid
      // (the nav menu drawer) also uses the bare class `.drawer__inner`, and
      // since the header renders before the cart drawer in the page, an
      // unscoped selector here would grab that unrelated drawer's inner
      // instead, silently moving the whole component into the wrong DOM
      // subtree (visible as the recommendations block floating detached
      // from the actual cart panel).
      var desktopSlot = document.querySelector('#CartDrawer .drawer__inner');
      if (desktopSlot && rec.parentNode !== desktopSlot) desktopSlot.insertBefore(rec, desktopSlot.firstChild);
    }
  }

  document.addEventListener('scroll', function (event) {
    var list = event.target.closest && event.target.closest('.cart-recommendations__list');
    if (!list) return;
    scrollPosition = { left: list.scrollLeft, top: list.scrollTop };
  }, true);

  function restoreScroll(list) {
    if (!scrollPosition) return;
    list.scrollLeft = scrollPosition.left;
    list.scrollTop = scrollPosition.top;
  }

  function addToCart(button) {
    if (button.disabled) return;
    button.disabled = true;
    button.classList.add('is-loading');

    var card = button.closest('.cart-recommendation-card');
    var cartDrawerEl = document.querySelector('cart-drawer');
    var sections = cartDrawerEl && typeof cartDrawerEl.getSectionsToRender === 'function'
      ? cartDrawerEl.getSectionsToRender()
      : [];

    var body = JSON.stringify({
      items: [{ id: parseInt(button.dataset.variantId, 10), quantity: 1 }],
      // CartDrawer.getSectionsToRender() (assets/cart-drawer.js) returns
      // {id, selector} — no .section field, that shape only exists on
      // CartDrawerItems/CartItems. Its own renderContents() looks sections
      // up by `.id` too (parsedState.sections[section.id]), matching how
      // product-form.js builds this same request. Mapping `.section` here
      // sent [null, null], so the server returned no matching sections and
      // renderContents() failed silently — item added to the real cart, UI
      // never updated.
      sections: sections.map(function (s) { return s.id; }),
      sections_url: window.location.pathname,
    });

    fetch(window.routes.cart_add_url, Object.assign({}, fetchConfig('javascript'), { body: body }))
      .then(function (response) { return response.json(); })
      .then(function (parsedState) {
        if (parsedState.status) {
          throw new Error(parsedState.description || (window.cartStrings && window.cartStrings.error) || 'Could not add to cart');
        }

        var wasPatched = patchCartDrawer(parsedState);
        if (!wasPatched && cartDrawerEl && typeof cartDrawerEl.renderContents === 'function') {
          cartDrawerEl.renderContents(parsedState);
        }

        // The product just added is now part of the cart, so the cart's
        // product signature changed — syncFromPatchedState triggers a full
        // rebuild of the list from the current cart contents, which no
        // longer recommends what's already in the cart. patchCartDrawer()
        // deliberately never touches .cart-recommendations itself (see its
        // own comment), so this only needs to run on that patched path; a
        // full renderContents() above already produces a fresh container
        // that the normal syncList() pass rebuilds on its own.
        if (card && wasPatched) {
          var list = card.closest('.cart-recommendations__list');
          if (list) syncFromPatchedState(parsedState, list);
        }

        button.disabled = false;
        button.classList.remove('is-loading');
        // Source MUST be "cart-items" — see the identical comment in
        // assets/cart-bundle.js. Any other value makes CartItems.onCartUpdate
        // (subscribed by <cart-drawer-items>) fetch /cart?section_id=main-cart
        // and splice that CART PAGE markup into the drawer — which is what
        // produced the "shows the right thing for a second, then flips to a
        // broken empty-cart look" bug: renderContents() above already did the
        // correct update; this second, unwanted fetch was overwriting it.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
        }
      })
      .catch(function (error) {
        button.disabled = false;
        button.classList.remove('is-loading');

        var errors = document.getElementById('cart-errors') ||
          document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = error.message;
      });
  }

  /*
   * Any change to the cart's distinct products fully rebuilds the list from
   * the current cart contents — no partial patching, no diffing against what
   * was there before. Already-fetched products resolve straight from
   * recsByProductId (no network); only genuinely new ones hit the network in
   * the rebuild that follows. A no-op when the cart's product set hasn't
   * actually changed (e.g. this same container just being recreated by an
   * unrelated cart edit like a quantity change), so it doesn't
   * refetch/rebuild on every mutation.
   */
  function syncList(list) {
    // Doubles as both "which cart products to build recommendations from"
    // and "which product ids to exclude from the results" — the same list
    // serves both roles, so there's only the one to read.
    var cartProductIds = (list.dataset.cartProductIds || '').split(',').filter(Boolean);

    STATE.meta = {
      excludeIds: cartProductIds,
      intent: list.dataset.intent || 'related',
      // 0 (blank setting, merchant hasn't set one) means no cap — see
      // fetchProductRecs.
      perItemLimit: parseInt(list.dataset.perItemLimit, 10) || 0,
      addToCartLabel: list.dataset.addToCartLabel || '',
      addToCartText: list.dataset.addToCartText || '',
    };

    if (!cartProductIds.length) {
      var wrapper = list.closest('.cart-recommendations');
      if (wrapper) wrapper.remove();
      STATE.cartSignature = '';
      return;
    }

    var signature = cartProductIds.join(',');
    var domIsFresh = !list.dataset.synced;
    if (signature === STATE.cartSignature && !domIsFresh) return;

    list.dataset.synced = 'true';
    STATE.cartSignature = signature;
    rebuildList(list, cartProductIds);
  }

  function rebuildList(list, cartProductIds) {
    if (STATE.observer) STATE.observer.disconnect();
    STATE.loadedForProductIds = [];
    STATE.pendingProductIds = cartProductIds.slice();
    STATE.renderedProductIds = {};
    list.innerHTML = '';

    ensureSentinel(list);
    fillIfNeeded(list).then(function () { restoreScroll(list); });
  }

  // How much of the queue to eagerly load (bypassing the scroll trigger) so
  // the list never renders emptier than it needs to. `loadedForProductIds.
  // length < EAGER_LOAD_COUNT` covers first paint; the scroll-size check
  // covers a cart product that came back with few/no usable
  // recommendations, which could otherwise leave the list too short to ever
  // become scrollable — stranding the sentinel below the fold where the
  // user could never reach it.
  function fillIfNeeded(list) {
    if (STATE.loading || !STATE.pendingProductIds.length) return Promise.resolve();
    var notYetScrollable = list.scrollHeight <= list.clientHeight + 4 &&
      list.scrollWidth <= list.clientWidth + 4;
    if (STATE.loadedForProductIds.length < EAGER_LOAD_COUNT || notYetScrollable) {
      return loadNextProduct(list).then(function () { return fillIfNeeded(list); });
    }
    return Promise.resolve();
  }

  function loadNextProduct(list) {
    if (STATE.loading || !STATE.pendingProductIds.length) return Promise.resolve();
    STATE.loading = true;
    var wrapper = list.closest('.cart-recommendations');
    if (wrapper) wrapper.classList.add('is-loading-more');

    var productId = STATE.pendingProductIds.shift();
    var excludeIds = STATE.meta.excludeIds.concat(Object.keys(STATE.renderedProductIds));

    return fetchProductRecs(productId, STATE.meta.intent, STATE.meta.perItemLimit, excludeIds).then(function (products) {
      var addedCount = 0;
      var fragment = document.createDocumentFragment();
      products.forEach(function (product) {
        var card = renderCard(product, STATE.meta.addToCartLabel, STATE.meta.addToCartText);
        if (!card) return;
        fragment.appendChild(card);
        addedCount++;
        STATE.renderedProductIds[String(product.id)] = true;
      });

      var sentinel = list.querySelector('.cart-recommendations__sentinel');
      if (sentinel) list.insertBefore(fragment, sentinel); else list.appendChild(fragment);

      STATE.loadedForProductIds.push(productId);
      STATE.loading = false;
      if (wrapper) wrapper.classList.remove('is-loading-more');

      // A cart product with zero usable recommendations (thin catalog,
      // everything already shown elsewhere) shouldn't dead-end the scroll
      // trigger.
      if (!addedCount && STATE.pendingProductIds.length) return loadNextProduct(list);
      maybeHideIfEmpty(list);
    }).catch(function () {
      STATE.loading = false;
      if (wrapper) wrapper.classList.remove('is-loading-more');
      maybeHideIfEmpty(list);
    });
  }

  // Nothing to show and nothing left to try (queue exhausted, no card ever
  // rendered) — hide the whole block rather than leaving an empty heading
  // with a blank scroll area.
  function maybeHideIfEmpty(list) {
    if (STATE.loading || STATE.pendingProductIds.length || STATE.loadedForProductIds.length) return;
    var wrapper = list.closest('.cart-recommendations');
    if (wrapper) wrapper.remove();
  }

  function ensureSentinel(list) {
    var sentinel = list.querySelector('.cart-recommendations__sentinel');
    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.className = 'cart-recommendations__sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      list.appendChild(sentinel);
    }

    if (STATE.observer) STATE.observer.disconnect();
    if (typeof IntersectionObserver === 'undefined') return;

    STATE.observer = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting && STATE.pendingProductIds.length && !STATE.loading) {
        loadNextProduct(list);
      }
    }, { root: list, rootMargin: '200px' });
    STATE.observer.observe(sentinel);
  }

  function init() {
    moveToViewportSlot();
    document.querySelectorAll('[data-cart-recommendations]').forEach(syncList);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('.cart-recommendation-card__add');
    if (!button) return;
    event.preventDefault();
    addToCart(button);
  });

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  // Live-resizing the browser with the drawer open (no cart mutation, so
  // nothing else here would otherwise fire) still needs to swap slots.
  if (typeof window.matchMedia === 'function') {
    var mobileQueryList = window.matchMedia(MOBILE_QUERY);
    var onViewportChange = function () { moveToViewportSlot(); };
    if (typeof mobileQueryList.addEventListener === 'function') {
      mobileQueryList.addEventListener('change', onViewportChange);
    } else if (typeof mobileQueryList.addListener === 'function') {
      mobileQueryList.addListener(onViewportChange); // Safari < 14
    }
  }
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, init);
  }

  // The cartUpdate pub/sub event isn't reliably ordered relative to the DOM
  // replacement that follows it — product-form.js and sofa-configurator.js
  // both publish() BEFORE calling cart-drawer's renderContents(), so init()
  // above can run against the OLD DOM (finds the not-yet-recreated
  // container, restores nothing useful) moments before renderContents()
  // swaps in a fresh one that then never gets processed until the next cart
  // update — visible as recommendations only updating after a hard reload.
  // Watching #CartDrawer directly catches the actual DOM change regardless
  // of publish/render ordering. syncList() is a cheap no-op reconciliation
  // when nothing's actually changed, so re-scanning on every mutation is
  // safe.
  var cartDrawerRoot = document.getElementById('CartDrawer');
  if (cartDrawerRoot && typeof MutationObserver !== 'undefined') {
    new MutationObserver(init).observe(cartDrawerRoot, { childList: true, subtree: true });
  }
})();
